import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

import CustomSelect from 'components/CustomSelect';
import CreateOrder from './modals/CreateOrder';
import Pagination from 'components/Pagination';
import FilterSearch from 'components/FilterSearch';
import FilterDropdown from 'components/FilterDropdown';
import DatePicker from 'react-datepicker';

import { GET, httpOk, POST } from 'helpers/api';
import { checkPermission, DOWNLOAD_FILE, formatDate, formatMoney, handleDateChange, sources } from 'helpers/helpers';

import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import OrderDetail from './modals/OrderDetail';
import ConfirmModal from 'components/ConfirmModal';

function Orders() {
    const { t } = useTranslation()

    const statuses = [
        {
            id: 'critical',
            name: t('critical')
        },
        {
            id: 'attention',
            name: t('attention')
        },
        {
            id: 'ok',
            name: t('ok')
        },
    ];

    const [dropdown, setDropdown] = useState(false)
    const [createOrderModal, setCreateOrderModal] = useState({ show: false, id: '' })
    const [orderDetailModal, setOrderDetailModal] = useState({ show: false, id: '' })
    const [confirmModal, setConfirmModal] = useState({ show: false, id: '' })

    const initFilterData = {
        page: 1,
        page_size: 20,
        date_from: '',
        date_to: '',
        search: '',
    }

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({})

    // async function processOnly(item) {
    //     const response = await POST(`/sales/orders/${item.id}/process-only/`)
    //     if (httpOk(response)) {
    //         getData();
    //     }
    // }

    function confirmAction() {
        if (confirmModal.reserve) {
            reserve()
        } else if (confirmModal.cancel) {
            cancel()
        } else {
            process()
        }
    }

    async function process() {
        const response = await POST(`/sales/orders/${confirmModal.item.id}/process/`)
        if (httpOk(response)) {
            setConfirmModal({ show: false })
            getData();
        }
    }

    async function shipDirect(item) {
        const response = await POST(`/sales/orders/${item.id}/ship/`)
        if (httpOk(response)) {
            getData();
        }
    }

    async function reserve() {
        const response = await POST(`/sales/orders/${confirmModal.item.id}/reserve/`)
        if (httpOk(response)) {
            setConfirmModal({ show: false })
            getData();
        }
    }

    async function cancel() {
        const response = await POST(`/sales/orders/${confirmModal.item.id}/cancel/`)
        if (httpOk(response)) {
            setConfirmModal({ show: false })
            getData();
        }
    }

    async function exportFile() {
        await DOWNLOAD_FILE('/inventory/fg-warehouse/items/export/', filterData, t('finished_goods'))
    }

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const [response, responseStats] = await Promise.all([
            GET('/sales/dashboard/', filterData),
            GET('/sales/orders/', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, ...responseStats.data });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <div className="d-between">
                <div>
                    <div className="main-content-title">
                        {t('orders_distributors')}
                    </div>

                    <div className="main-content-subtitle">
                        {t('orders_shipments_management')}
                    </div>
                </div>

                <div className="d-flex gap-8">
                    <button className="dark gap-16" onClick={() => setCreateOrderModal({ show: true })}>
                        <PlusIcon />
                        {t('create_order')}
                    </button>
                </div>
            </div>

            <div className="d-flex  gap-16">
                <div className="card w-100">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_orders')}
                            </div>
                            <div className="value ">
                                {formatMoney(data?.count)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card w-100">
                    <div>
                        <div className="title">
                            {t('new')}
                        </div>
                        <div className="value text-primary">
                            {formatMoney(data?.counters?.new_count)}
                        </div>
                    </div>
                </div>

                <div className="card w-100">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_processing')}
                            </div>
                            <div className="value text-warning">
                                {formatMoney(data?.counters?.processing_count)}
                            </div>
                        </div>
                    </div>
                </div>

                {checkPermission("prices.view.full") &&
                    <div className="card w-100">
                        <div className="d-between">
                            <div>
                                <div className="title">
                                    {t('total_amount')}
                                </div>
                                <div className="value">
                                    {formatMoney(data?.counters?.total_amount)}
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>

            <div className="card">
                <div className="d-flex gap-16 w-100">
                    <FilterSearch
                        value={filterData.search}
                        onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
                        placeholder={t('search_by_sku_or_name')}
                    />

                    <CustomSelect
                        options={statuses}
                        value={statuses.find(option => option.id === filterData?.status) || ''}
                        onChange={(option) => setFilterData({ ...filterData, status: option?.id })}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        placeholder={t('select_status')}
                    />

                    <div style={{ minWidth: '250px' }}>
                        <CustomSelect
                            options={sources}
                            value={sources.find(option => option.id === filterData?.source) || ''}
                            onChange={(option) => setFilterData({ ...filterData, source: option?.id })}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            placeholder={t('select_source')}
                        />
                    </div>

                    <FilterDropdown dropdown={dropdown} setDropdown={setDropdown}>
                        <div className="grid2 gap-8">
                            <div className="form-group">
                                <label htmlFor="name">{t('date_from')}</label>
                                <DatePicker
                                    portalId="root"
                                    selected={filterData.date_from ? new Date(filterData.date_from) : null}
                                    onChange={(date) => setFilterData(handleDateChange(date, 'date_from', filterData))}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText={t('select_date')}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="name">{t('date_to')}</label>
                                <DatePicker
                                    portalId="root"
                                    selected={filterData.date_to ? new Date(filterData.date_to) : null}
                                    onChange={(date) => setFilterData(handleDateChange(date, 'date_to', filterData))}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText={t('select_date')}
                                />
                            </div>
                        </div>
                    </FilterDropdown>

                    <button className="white third" onClick={() => exportFile()}>
                        <DownloadIcon />
                        {t('export')}
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('orders_list')}
                    </div>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('order_number')}</th>
                            <th style={{ width: '10%' }}>{t('source')}</th>
                            <th style={{ width: '20%' }}>{t('distributor')}</th>
                            <th style={{ width: '10%' }}>{t('created_at')}</th>
                            {checkPermission("prices.view.full") &&
                                <th style={{ width: '10%' }}>{t('amount')}</th>
                            }
                            <th style={{ width: '10%' }}>{t('status')}</th>
                            <th style={{ width: '10%' }}>{t('actions')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.results?.map((item, index) => (
                            <tr key={index}>
                                <td>{item.order_number}</td>
                                <td>
                                    <div className={`source source-${item.source}`}>
                                        {t(item.source)}
                                    </div>
                                </td>
                                <td>{item.distributor_name}</td>
                                <td>{formatDate(item.created_at)}</td>
                                {checkPermission("prices.view.full") &&
                                    <td>{formatMoney(item.total_amount)}</td>
                                }
                                <td>
                                    <div className={`alert alert-${item.status} w-fit-content`}>
                                        {t(item.status)}
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center gap-8">
                                        <button className="transparent small" onClick={() => setOrderDetailModal({ show: true, id: item.id })}>
                                            {t('details')}
                                        </button>
                                        {(item?.allowed_actions?.includes('cancel') && checkPermission("sales.order.cancel")) &&
                                            <button className="white danger small" onClick={() => setConfirmModal({ show: true, item, cancel: true })}>
                                                {t('cancel')}
                                            </button>
                                        }
                                        {(item?.allowed_actions?.includes('prepare') && checkPermission("sales.order.prepare")) &&
                                            <button className="white small" onClick={() => setConfirmModal({ show: true, item })}>
                                                {t('process')}
                                            </button>
                                        }
                                        {(item?.allowed_actions?.includes('ship') && checkPermission("inventory.finished.ship")) &&
                                            <button className="white small" onClick={() => shipDirect(item)}>
                                                {t('ship')}
                                            </button>
                                        }
                                        {(item?.allowed_actions?.includes('reserve') && checkPermission("sales.order.reserve")) &&
                                            <button className="white small" onClick={() => setConfirmModal({ show: true, item, reserve: true })} >
                                                {t('reserve')}
                                            </button>
                                        }

                                        {/* {item?.allowed_actions?.includes('process') &&
                                            <button className="white small" onClick={() => processOnly(item)}>
                                                {t('process')}
                                            </button>
                                        } */}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />

                <CreateOrder modal={createOrderModal.show} setModal={() => setCreateOrderModal({ show: false })} getData={getData} id={createOrderModal.id} />
                <OrderDetail modal={orderDetailModal.show} setModal={() => setOrderDetailModal({ show: false })} id={orderDetailModal.id} />
                <ConfirmModal modal={confirmModal.show} setModal={() => setConfirmModal({ show: false })} confirm={() => confirmAction()} />
            </div>
        </>
    )
}

export default Orders
